import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { AdminShell } from "@/components/admin/admin-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiGet, apiPatch } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type VendorDetail = {
  vendor: {
    id: string;
    businessName: string;
    slug: string;
    ownerName: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    description: string | null;
    status: string;
    commissionRate: number;
    vendorType: { name: string } | null;
    user: { email: string; name: string } | null;
  };
  stats: { grossSales: number; netEarnings: number; transactions: number; orders: number };
};

export function AdminVendorDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState<VendorDetail | null>(null);
  const [commission, setCommission] = useState("");

  async function load() {
    const d = await apiGet<VendorDetail>(`/api/admin/vendors/${id}`);
    setData(d);
    if (d) setCommission(String(d.vendor.commissionRate));
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function saveCommission() {
    try {
      await apiPatch(`/api/admin/vendors/${id}`, { commissionRate: Number(commission) });
      toast.success("Commission updated");
      void load();
    } catch {
      toast.error("Could not update");
    }
  }

  async function act(action: string) {
    try {
      await apiPatch(`/api/admin/vendors/${id}/${action}`, {});
      toast.success("Updated");
      void load();
    } catch {
      toast.error("Action failed");
    }
  }

  if (!data) return <AdminShell title="Vendor">Loading…</AdminShell>;
  const { vendor, stats } = data;

  return (
    <AdminShell title={vendor.businessName} subtitle={vendor.vendorType?.name ?? "Vendor"}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={vendor.status === "ACTIVE" ? "success" : "prime"}>{vendor.status}</Badge>
          <Button onClick={() => act("approve")}>Approve</Button>
          <Button onClick={() => act("activate")}>Activate</Button>
          <Button onClick={() => act("suspend")}>Suspend</Button>
          <Button onClick={() => act("reject")}>Reject</Button>
        </div>

        <div className="amazon-card p-5 space-y-1 text-sm">
          <p>Owner: {vendor.ownerName ?? "—"}</p>
          <p>Email: {vendor.user?.email ?? vendor.email ?? "—"}</p>
          <p>Phone: {vendor.phone ?? "—"}</p>
          <p>Address: {vendor.address ?? "—"}</p>
          <p className="text-slate-500">{vendor.description}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="Gross sales" value={formatPrice(stats.grossSales)} />
          <Stat label="Net earnings" value={formatPrice(stats.netEarnings)} />
          <Stat label="Orders" value={String(stats.orders)} />
          <Stat label="Transactions" value={String(stats.transactions)} />
        </div>

        <div className="amazon-card flex items-center gap-3 p-5">
          <label className="text-sm font-bold">Commission rate (%)</label>
          <input
            type="number"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
            className="w-24 rounded border border-slate-300 px-2 py-1 dark:border-white/10 dark:bg-white/5"
          />
          <Button onClick={() => void saveCommission()}>Save</Button>
        </div>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="amazon-card p-4">
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-black">{value}</p>
    </div>
  );
}
